import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from './Icon';

/**
 * Botoes do painel (RNF15).
 *
 * `primary` e o vermelho da marca e existe UM por bloco de acao: dois botoes
 * cheios lado a lado deixam de dizer qual e a acao principal. `secondary` tem
 * contorno (as "linhas de contorno" do visual premium), `ghost` e texto, e
 * `danger` fica reservado para excluir/desligar. Estado de carregamento troca
 * o icone por um ponto pulsando e desabilita o clique -- clique duplo num
 * "Iniciar envio" e um disparo em dobro.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-all duration-150 active:scale-[0.98] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100';

const VARIANT: Readonly<Record<ButtonVariant, string>> = {
  primary: 'bg-brand text-white shadow-soft hover:bg-brand/90',
  accent: 'bg-accent text-white shadow-soft hover:bg-accent/90',
  secondary: 'border border-line bg-surface text-ink shadow-soft hover:border-brand/60 hover:text-brand',
  ghost: 'text-ink-muted hover:bg-ink/5 hover:text-ink',
  danger: 'border border-status-critical/40 bg-status-critical/10 text-status-critical hover:bg-status-critical/20',
};

const SIZE: Readonly<Record<ButtonSize, string>> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
};

interface CommonProps {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly icon?: IconName;
  readonly iconRight?: IconName;
  readonly loading?: boolean;
  readonly className?: string;
  readonly children?: ReactNode;
}

export type ButtonProps = CommonProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>;

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  loading = false,
  className = '',
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button type={type} disabled={disabled === true || loading} className={`${BASE} ${VARIANT[variant]} ${SIZE[size]} ${className}`} {...rest}>
      {loading ? <span aria-hidden="true" className="h-2 w-2 animate-pulse rounded-full bg-current" /> : icon === undefined ? null : <Icon name={icon} size={size === 'sm' ? 'xs' : 'sm'} />}
      {children}
      {iconRight === undefined ? null : <Icon name={iconRight} size={size === 'sm' ? 'xs' : 'sm'} />}
    </button>
  );
}

/** Mesmo visual de botao, mas navegando: para "Abrir", "Ver todos", "Nova campanha". */
export function LinkButton({
  to,
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  className = '',
  children,
  keepSearch = true,
}: CommonProps & { readonly to: string; readonly keepSearch?: boolean }): JSX.Element {
  return (
    <Link to={keepSearch ? { pathname: to, search: window.location.search } : to} className={`${BASE} ${VARIANT[variant]} ${SIZE[size]} ${className}`}>
      {icon === undefined ? null : <Icon name={icon} size={size === 'sm' ? 'xs' : 'sm'} />}
      {children}
      {iconRight === undefined ? null : <Icon name={iconRight} size={size === 'sm' ? 'xs' : 'sm'} />}
    </Link>
  );
}

/** Botao so de icone. `label` e obrigatorio: vira o nome acessivel e o title. */
export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  className = '',
  ...rest
}: { readonly icon: IconName; readonly label: string; readonly variant?: ButtonVariant; readonly size?: ButtonSize; readonly className?: string } & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'children' | 'aria-label' | 'title'
>): JSX.Element {
  const dim = size === 'sm' ? 'h-8 w-8' : size === 'lg' ? 'h-11 w-11' : 'h-9 w-9';
  return (
    <button type="button" aria-label={label} title={label} className={`${BASE} ${VARIANT[variant]} ${dim} px-0`} {...rest}>
      <Icon name={icon} size="sm" className={className} />
    </button>
  );
}
